import React, { useState, useEffect, useCallback } from "react";
import { Plus, Download } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { UserList } from "./UserList";
import { UserManagementFilters } from "./UserManagementFilters";
import { UserForm } from "./UserForm";
import { useUserManagementRedux } from "../../hooks/useUserManagementRedux";
import { useDispatch } from "react-redux";
import { setUsers } from "../../../../redux/UserManagement/actions";
import type {
  PlatformUser,
  UserRole,
  UserStatus,
  UserFilters,
  UserBulkAction,
} from "../../types/user";
import { Modal } from "antd";
import { Notification as customNotification } from "../../../../components/ui/custom-notification";
import axiosConfig from "../../../../redux/UserManagement/axiosConfig";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
} from "../../../../components/ui/dialog";
import { Shield } from "lucide-react";
import { UserActionsMenu } from "./UserActionsMenu";
import {
  deleteBulkUser,
  updateBulkUser,
} from "../../../../redux/UserManagement/saga";
import { logActivityExport } from "../../../audit/activityAudit";

interface UserManagementProps {
  currentUser: PlatformUser;
}

export const UserManagement: React.FC<UserManagementProps> = ({
  currentUser,
}) => {
  const {
    users,
    selectedUsers,
    filters,
    loading,
    error,
    addUserLoading,
    updateUserLoading,
    deleteUserLoading,
    bulkActionLoading,
    resetPasswordLoading,
    toggleTwoFactorLoading,
    getFilteredUsers,
    loadUsers,
    addUser,
    updateUser,
    deleteUser,
    bulkAction,
    resetPassword,
    toggleTwoFactor,
  } = useUserManagementRedux();
  const dispatch = useDispatch();

  // Fetch departments and companies from API

  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PlatformUser | undefined>();
  const [localSelectedUsers, setLocalSelectedUsers] = useState<string[]>([]);
  const [localFilters, setLocalFilters] = useState<UserFilters>({});
  const [filteredUsers, setFilteredUsers] = useState<PlatformUser[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] =
    useState(false);

  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [resetUser, setResetUser] = useState<PlatformUser | null>(null);
  const [resetExpirySeconds, setResetExpirySeconds] = useState<number>(1800); // default 30 min (in seconds)

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDisableModalVisible, setIsDisableModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState<PlatformUser | null>(null);
  const [userToDisable, setUserToDisable] = useState<PlatformUser | null>(null);
  const [isDisableLoading, setIsDisableLoading] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<UserStatus | null>(null);
  const [isBulkOperation, setIsBulkOperation] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Fetch all users from API; client-side localFilters apply in filterUsers
  const refreshUserList = useCallback(() => {
    loadUsers();
  }, [loadUsers]);

  const showDeleteModal = (user: PlatformUser) => {
    setUserToDelete(user);
    setIsBulkDelete(false);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteClick = (user: PlatformUser) => {
    showDeleteModal(user);
  };
  const handleCancel = () => {
    setIsDeleteModalVisible(false);
    setUserToDelete(null);
    setIsBulkDelete(false);
  };

  const handleConfirmDelete = async () => {
    try {
      let success = false;
      if (isBulkDelete) {
        if (localSelectedUsers.length > 0) {
          const result = await deleteBulkUser(localSelectedUsers, true);
          if (result !== null) {
            success = true;
            setLocalSelectedUsers([]);
            setLocalFilters({});
          }
        }
      } else if (userToDelete) {
        const result = await deleteBulkUser([userToDelete.id], false);
        if (result !== null) {
          success = true;
          setLocalFilters({});
        }
      }
      setIsDeleteModalVisible(false);
      setUserToDelete(null);
      setIsBulkDelete(false);
      if (success) {
        refreshUserList();
      }
    } catch (error) {}
  };

  const showDisableModal = (user: PlatformUser) => {
    if (user.status === "pending") {
      customNotification.warning(
        "Can not change status",
        "User is still in pending approval state"
      );
      return;
    }
    setUserToDisable(user);
    setIsBulkOperation(false);
    setIsDisableModalVisible(true);
  };

  const handleCancelDisable = () => {
    setIsDisableModalVisible(false);
    setUserToDisable(null);
    setIsBulkOperation(false);
    setBulkStatus(null);
  };

  const handleConfirmDisable = async () => {
    setIsDisableLoading(true);
    try {
      let success = false;
      if (isBulkOperation) {
        if (localSelectedUsers.length > 0 && bulkStatus) {
          const result = await updateBulkUser({
            userIds: localSelectedUsers,
            status: bulkStatus,
          });
          if (result !== null) {
            success = true;
            setLocalSelectedUsers([]);
          }
        }
      } else if (userToDisable) {
        const result = await updateBulkUser({
          userIds: [userToDisable.id],
          status:
            userToDisable.status === "active" ||
            userToDisable.status === "pending"
              ? "inactive"
              : "active",
        });
        if (result !== null) {
          success = true;
        }
      }
      setIsDisableModalVisible(false);
      setUserToDisable(null);
      setIsBulkOperation(false);
      setBulkStatus(null);
      if (success) {
        refreshUserList();
      }
    } catch (error) {
    } finally {
      setIsDisableLoading(false);
    }
  };

  // Custom filtering function that uses localFilters
  const filterUsers = useCallback(
    (users: PlatformUser[], filters: UserFilters) => {
      return users.filter((user) => {
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          const matchesSearch =
            user.name.toLowerCase().includes(searchTerm) ||
            user.email.toLowerCase().includes(searchTerm) ||
            user.profile.department?.name?.toLowerCase().includes(searchTerm) ||
            user.profile.company?.name?.toLowerCase().includes(searchTerm);
          if (!matchesSearch) return false;
        }

        if (filters.roles?.length && !filters.roles.includes(user.role)) {
          return false;
        }

        if (filters.status?.length && !filters.status.includes(user.status)) {
          return false;
        }

        if (
          filters.department?.length &&
          !filters.department.includes(user.profile.department?.name || "")
        ) {
          return false;
        }

        if (
          filters.company?.length &&
          !filters.company.includes(user.profile.company?.name || "")
        ) {
          return false;
        }

        return true;
      });
    },
    []
  );

  // Update filtered users when users or filters change
  useEffect(() => {
    const filtered = filterUsers(users, localFilters);
    setFilteredUsers(filtered);
  }, [users, localFilters, filterUsers]);

  useEffect(() => {
    if (users.length > 0) {
      const loadCategories = async () => {
        const departments = Array.from(
          new Set(
            users
              .filter((user) => user.profile.department?.name)
              .map((user) => user.profile.department?.name)
          )
        ).map((name) => ({ id: name, name }));
        const companies = Array.from(
          new Set(
            users
              .filter((user) => user.profile.company?.name)
              .map((user) => user.profile.company?.name)
          )
        ).map((name) => ({ id: name, name }));
        setDepartments(departments);
        setCompanies(companies);
        setCategoriesLoading(false);
      };
      loadCategories();
    }
  }, [users]);

  const handleAddUser = () => {
    setSelectedUser(undefined);
    setShowUserForm(true);
  };

  const handleEditUser = (user: PlatformUser) => {
    setSelectedUser(user);
    setIsEdit(true);
    setResetPasswordModalVisible(false);
    setShowUserForm(true);
  };

  const handleFormSubmit = async (updateResult?: {
    id?: string;
    user_extra_details?: PlatformUser["user_extra_details"];
  }) => {
    if (updateResult?.id && updateResult.user_extra_details !== undefined) {
      dispatch(
        setUsers({
          usersData: users.map((u) =>
            u.id === updateResult.id
              ? { ...u, user_extra_details: updateResult.user_extra_details }
              : u
          ),
        })
      );
    }
    setIsEdit(false);
    setResetPasswordModalVisible(false);
    setShowUserForm(false);
    setSelectedUser(undefined);
    refreshUserList();
  };

  const handleFormCancel = () => {
    setShowUserForm(false);
    setResetPasswordModalVisible(false);
    setIsEdit(false);
    setSelectedUser(undefined);
  };

  const handleUserSelect = (userId: string) => {
    setLocalSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    setLocalSelectedUsers((prev) =>
      prev.length === users.length ? [] : users.map((u) => u.id)
    );
  };

  const handleBulkAction = async (
    action: UserBulkAction["action"],
    value: any
  ) => {
    if (localSelectedUsers.length === 0) return;

    try {
      if (action === "status") {
        const pendingUsers = localSelectedUsers.filter((id) => {
          const user = users.find((user) => user.id === id);
          return user?.status === "pending";
        });

        if (pendingUsers.length > 0) {
          customNotification.warning(
            "Can not change status",
            "Selected users are still pending approval"
          );
          setIsDisableLoading(false);
          return;
        }

        setBulkStatus(value);
        setUserToDisable(null);
        setIsBulkOperation(true);
        setIsDisableModalVisible(true);
        
      } else if (action === "delete") {
        setUserToDelete(null);
        setIsBulkDelete(true);
        setIsDeleteModalVisible(true);
      }
      //setLocalSelectedUsers([]);
      //loadUsers(localFilters);
    } catch (error) {
      // In a real app, show error notification
    }
  };

  const handleToggleStatus = async (userId: string, isDisabled: boolean) => {
    refreshUserList();
  };

  const departmentOptions = departments.map((dept) => ({
    value: dept.name,
    label: dept.name,
  }));

  const companyOptions = companies.map((comp) => ({
    value: comp.name,
    label: comp.name,
  }));

  const handleExportUsers = () => {
    const exportData = users.map((user) => ({
      "Full Name": user.name,
      "Email Address": user.email,
      "User Role": user.role,
      "Account Status": user.status,
      "Password Change Required": user.requirePasswordChange ? "Yes" : "No",
      Department: user.profile.department?.name || "",
      Company: user.profile.company?.name || "",
      "Last Login Date": new Date(user.lastLogin).toLocaleDateString(),
    }));

    const csv = [
      Object.keys(exportData[0]).join(","),
      ...exportData.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Data 360 Users.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    logActivityExport('admin-users-csv', { count: users.length });
  };

  const handleResetPassword = (user: PlatformUser) => {
    setResetUser(user);
    setResetModalVisible(true);
  };

  const handleCancelReset = () => {
    setResetModalVisible(false);
    setResetUser(null);
    setResetExpirySeconds(1800);
  };

  const handleConfirmReset = async () => {
    try {
      if (resetUser) {
        const res = await resetPassword(
          resetUser.email,
          resetExpirySeconds,
          "Data360"
        );
        console.log("Reset password response:", res);
        // customNotification.success(
        //   "Reset Link Sent",
        //   `An invite link has been sent to ${resetUser.email}`
        // );
      }
    } catch (error) {
      customNotification.error("Reset Failed", "Unable to send reset link.");
    } finally {
      handleCancelReset();
      refreshUserList();
    }
  };

  // If showing user form, render it as a full page
  if (showUserForm) {
    return (
      <UserForm
        user={selectedUser}
        resetPasswordModalVisible={resetPasswordModalVisible}
        isEdit={isEdit}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        onBack={handleFormCancel}
        loading={addUserLoading || updateUserLoading}
      />
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:justify-between sm:items-center">
        <h2 className="text-lg font-semibold text-blueprint-blue">User Management</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportUsers}
            className="flex items-center gap-2  hover:shadow-md transition-all duration-300 hover:scale-105"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            onClick={handleAddUser}
            size="sm"
            className="flex items-center gap-2 hover:shadow-md transition-all duration-300 hover:scale-105"
            disabled={addUserLoading}
          >
            <Plus className="h-4 w-4" />
            {addUserLoading ? "Adding..." : "Add User"}
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {/* {error && (
        <div className="mb-4 p-4 bg-red-50 border rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )} */}

      <div className="mb-6">
        <UserManagementFilters
          filters={localFilters}
          onFiltersChange={setLocalFilters}
          departmentOptions={departmentOptions}
          companyOptions={companyOptions}
          categoriesLoading={categoriesLoading}
          filteredCount={filteredUsers.length}
        />
      </div>

      {/* Bulk Actions */}
      {localSelectedUsers.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-gray-600">
              {localSelectedUsers.length} users selected
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                onValueChange={(value) => handleBulkAction("status", value)}
                disabled={bulkActionLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Change Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Enable Users</SelectItem>
                  <SelectItem value="inactive">Disable Users</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={() => handleBulkAction("delete", null)}
                disabled={bulkActionLoading}
              >
                {bulkActionLoading ? "Processing..." : "Delete Selected"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <UserList
        users={filteredUsers}
        selectedUsers={localSelectedUsers}
        onSelectUser={handleUserSelect}
        onSelectAll={handleSelectAll}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteClick}
        onResetPassword={handleResetPassword}
        onToggleStatus={showDisableModal}
        currentUser={currentUser}
        loading={loading}
        deleteUserLoading={deleteUserLoading}
        resetPasswordLoading={resetPasswordLoading}
        toggleTwoFactorLoading={toggleTwoFactorLoading}
      />

      <Dialog
        open={isDeleteModalVisible}
        onOpenChange={setIsDeleteModalVisible}
      >
        <DialogContent className="max-w-[750px] p-6">
          <div className="flex justify-between items-center mb-6">
            <DialogTitle className="text-xl font-semibold">
              {isBulkDelete ? "Bulk Delete Users" : "Delete User"}
            </DialogTitle>
          </div>
          <div className="mb-6">
            {isBulkDelete ? (
              <>
                <p>
                  Are you sure you want to{" "}
                  <b style={{ color: "#EF314C" }}>delete</b> the accounts of
                  <b style={{ color: "#EF314C" }}>
                    {" "}
                    {localSelectedUsers.length} selected users
                  </b>{" "}
                  from the application?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  This action is irreversible. All selected users will no longer
                  be able to log in to the application.
                  <br />
                  This operation cannot be undone.
                </p>
              </>
            ) : (
              <>
                <p>
                  Are you sure you want to{" "}
                  <b style={{ color: "#EF314C" }}>delete</b> the account of
                  <b style={{ color: "#EF314C" }}> {userToDelete?.name}</b> from
                  the application?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  This action is irreversible. The user will no longer be able
                  to log in to the application.
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={deleteUserLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} disabled={deleteUserLoading}>
              {deleteUserLoading
                ? isBulkDelete
                  ? "Deleting Users..."
                  : "Deleting..."
                : isBulkDelete
                ? "Delete Users"
                : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetModalVisible} onOpenChange={setResetModalVisible}>
        <DialogContent className="max-w-[800px] p-6">
          <div className="flex justify-between items-center mb-4">
            <DialogTitle className="text-xl font-semibold">
              Reset Password
            </DialogTitle>
          </div>

          <div className="mb-6">
            <p className="text-gray-700">
              Are you sure you want to{" "}
              <span className="text-[#EF314C] font-semibold">
                reset the password
              </span>{" "}
              for
              <span className="text-[#EF314C] font-semibold">
                {" "}
                {resetUser?.name}
              </span>
              ?
            </p>
            <p className="text-sm text-gray-500 mt-3">
              The user will receive an invite email with a link to set a new
              password.
            </p>

            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link Expiry Time
              </label>
              <div className="flex gap-3">
                <Select
                  value={resetExpirySeconds.toString()}
                  onValueChange={(value) =>
                    setResetExpirySeconds(Number(value))
                  }
                >
                  <SelectTrigger className="w-fit">
                    <SelectValue placeholder="Select Expiry Time" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* <SelectItem value="900">15 minutes</SelectItem> */}
                    <SelectItem value="1800">30 minutes</SelectItem>
                    <SelectItem value="3600">1 hour</SelectItem>
                    {/* <SelectItem value="7200">2 hours</SelectItem> */}
                    {/* <SelectItem value="14400">4 hours</SelectItem> */}
                    <SelectItem value="86400">24 hours</SelectItem>
                    <SelectItem value="172800">2 Days</SelectItem>
                    <SelectItem value="604800">7 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={handleCancelReset}
              disabled={resetPasswordLoading}
              className="min-w-24"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReset}
              disabled={resetPasswordLoading}
              className="min-w-32 bg-[#EF314C] hover:bg-[#d42a43]"
            >
              {resetPasswordLoading ? "Sending..." : "Send Reset Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDisableModalVisible}
        onOpenChange={setIsDisableModalVisible}
      >
        <DialogContent className="max-w-[750px] p-6">
          <div className="flex justify-between items-center mb-6">
            <DialogTitle className="text-xl font-semibold">
              {isBulkOperation
                ? `Bulk ${bulkStatus === "active" ? "Enable" : "Disable"} Users`
                : userToDisable?.status === "active" ||
                  userToDisable?.status === "pending"
                ? "Disable User"
                : "Enable User"}
            </DialogTitle>
          </div>

          <div className="mb-6">
            {isBulkOperation ? (
              <>
                <p>
                  Are you sure you want to{" "}
                  <b style={{ color: "#EF314C" }}>
                    {bulkStatus === "active" ? "enable" : "disable"}
                  </b>{" "}
                  the accounts of
                  <b style={{ color: "#EF314C" }}>
                    {" "}
                    {localSelectedUsers.length} selected users
                  </b>
                  ?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {bulkStatus === "active"
                    ? "The selected users will be re-enabled and able to log in to the application."
                    : "The selected users will be temporarily deactivated and unable to log in."}
                  <br />
                  {bulkStatus === "active"
                    ? "Admin can disable these accounts at any time from the user management panel."
                    : "Admin can re-enable these accounts at any time from the user management panel."}
                </p>
              </>
            ) : (
              <>
                <p>
                  Are you sure you want to{" "}
                  <b style={{ color: "#EF314C" }}>
                    {userToDisable?.status === "active" ||
                    userToDisable?.status === "pending"
                      ? "disable"
                      : "enable"}
                  </b>{" "}
                  the account of
                  <b style={{ color: "#EF314C" }}> {userToDisable?.name}</b>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {userToDisable?.status === "active" ||
                  userToDisable?.status === "pending"
                    ? "The user will be temporarily deactivated and unable to log in."
                    : "The user will be re-enabled and able to log in."}
                  <br />
                  {userToDisable?.status === "active" ||
                  userToDisable?.status === "pending"
                    ? "Admin can re-enable this account at any time from the user management panel."
                    : "Admin can disable this account at any time from the user management panel."}
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelDisable}
              disabled={isDisableLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDisable}
              disabled={isDisableLoading}
              loading={isDisableLoading}
              loadingText={isBulkOperation ? "Processing..." : "Disabling..."}
            >
              {isDisableLoading
                ? isBulkOperation
                  ? "Processing..."
                  : "Disabling..."
                : isBulkOperation
                ? `${bulkStatus === "active" ? "Enable" : "Disable"} Users`
                : userToDisable?.status === "active" ||
                  userToDisable?.status === "pending"
                ? "Disable User"
                : "Enable User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
